import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, IconButton, MenuItem, TextField } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Account, Segment } from '../../interfaces/accounting.interface';
import {
    RecurrenceFrequency,
    RecurringJournalLine,
    RecurringMode,
} from '../../interfaces/recurringJournal.interface';
import { RecurringJournalService } from '../../services/recurring-journal.service';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import OrderPicker from '../costing/OrderPicker';
import { useOrderTagging } from '../costing/useOrderTagging';
import { SegmentSettingsService } from '../../services/segment-settings.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { postingAccounts } from '../../utils/accountHierarchy';
import { isSegmentRequired } from './JournalEntryCreate';
import { countDueOccurrences, firstOccurrence } from './recurringJournalText';
import { formatDate } from '../../utils/dateUtils';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import ProjectSelect, { projectTagLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project

const th = { padding: '12px', textAlign: 'left' as const, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
const td = { padding: '12px', borderBottom: '1px solid #ddd' };

interface MakeRecurringSource {
    sourceEntryId?: string;
    description?: string;
    lines?: RecurringJournalLine[];
}

const blankLines = (): RecurringJournalLine[] => [
    { accountId: '', entryType: 'DEBIT', amount: 0, description: '' },
    { accountId: '', entryType: 'CREDIT', amount: 0, description: '' },
];

const RecurringJournalForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    const orderTagging = useOrderTagging(); // B4-0 (0-5)
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const source = (location.state as { makeRecurringFrom?: MakeRecurringSource } | null)?.makeRecurringFrom;

    const [accounts, setAccounts] = useState<Account[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [defaultSegmentId, setDefaultSegmentId] = useState('');
    const [name, setName] = useState(source?.description ?? '');
    const [description, setDescription] = useState(source?.description ?? '');
    const [frequency, setFrequency] = useState<RecurrenceFrequency>('MONTHLY');
    const [intervalCount, setIntervalCount] = useState(1);
    const [dayChoice, setDayChoice] = useState<string>('1'); // '1'..'28' | 'LAST'
    const [startDate, setStartDate] = useState(toLocalIsoDate());
    const [endDate, setEndDate] = useState('');
    const [maxOccurrences, setMaxOccurrences] = useState('');
    const [mode, setMode] = useState<RecurringMode>('DRAFT');
    const [sourceEntryId, setSourceEntryId] = useState<string | undefined>(source?.sourceEntryId);
    const [lines, setLines] = useState<RecurringJournalLine[]>(
        source?.lines && source.lines.length >= 2
            ? source.lines.map(l => ({ ...l, description: l.description ?? '' }))
            : blankLines());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        AccountService.getAllAccounts().then(setAccounts).catch(() => setAccounts([]));
        SegmentService.getByInstitution()
            .then(res => setSegments((res.content ?? []).filter((s: Segment) => s.active !== false)))
            .catch(() => setSegments([]));
        SegmentSettingsService.get().then(s => setDefaultSegmentId(s.defaultSegmentId ?? '')).catch(() => setDefaultSegmentId(''));
    }, []);

    useEffect(() => {
        if (!id) return;
        RecurringJournalService.get(id).then(t => {
            setName(t.name);
            setDescription(t.description ?? '');
            setFrequency(t.frequency);
            setIntervalCount(t.intervalCount);
            setDayChoice(t.lastDayOfMonth ? 'LAST' : String(t.dayOfMonth ?? 1));
            setStartDate(t.startDate);
            setEndDate(t.endDate ?? '');
            setMaxOccurrences(t.maxOccurrences != null ? String(t.maxOccurrences) : '');
            setMode(t.mode);
            setSourceEntryId(t.sourceEntryId ?? undefined);
            setLines(t.lines.map(l => ({ ...l, description: l.description ?? '' })));
        }).catch(err => toast.showApiError(err, 'Failed to load recurring journal'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const leafAccounts = useMemo(() => postingAccounts(accounts), [accounts]);
    const totalDebits = lines.filter(l => l.entryType === 'DEBIT').reduce((s, l) => s + Number(l.amount || 0), 0);
    const totalCredits = lines.filter(l => l.entryType === 'CREDIT').reduce((s, l) => s + Number(l.amount || 0), 0);

    const setLine = (i: number, patch: Partial<RecurringJournalLine>) =>
        setLines(prev => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) { toast.showError('Give the recurring journal a name'); return; }
        if (Math.abs(totalDebits - totalCredits) > 0.001) { toast.showError('Total debits must equal total credits'); return; }
        if (lines.some(l => !l.accountId || !(Number(l.amount) > 0))) {
            toast.showError('All lines must have an account selected and a positive amount');
            return;
        }
        if (lines.some(l => isSegmentRequired(l.accountId, accounts) && !l.segmentId)) {
            toast.showError('Select a segment for every line whose account requires one');
            return;
        }
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const monthly = frequency !== 'WEEKLY';
        if (!id) {
            // Back-dated start: warn before flooding the books with catch-up entries.
            const first = firstOccurrence(frequency, monthly && dayChoice !== 'LAST' ? Number(dayChoice) : null,
                monthly && dayChoice === 'LAST', startDate);
            const due = countDueOccurrences({
                frequency, intervalCount, dayOfMonth: monthly && dayChoice !== 'LAST' ? Number(dayChoice) : null,
                lastDayOfMonth: monthly && dayChoice === 'LAST', endDate: endDate || null,
                maxOccurrences: maxOccurrences ? Number(maxOccurrences) : null, occurrencesProcessed: 0,
            }, first, toLocalIsoDate());
            if (due.count > 3 && !window.confirm(
                `This start date is in the past: ${due.count} journals dated from ${formatDate(due.first!)} up to today ` +
                `will be generated (12 per run). Continue?`)) {
                return;
            }
        }
        const req = {
            institutionId,
            name: name.trim(),
            description: description.trim() || undefined,
            frequency,
            intervalCount,
            dayOfMonth: monthly && dayChoice !== 'LAST' ? Number(dayChoice) : null,
            lastDayOfMonth: monthly && dayChoice === 'LAST',
            startDate,
            endDate: endDate || null,
            maxOccurrences: maxOccurrences ? Number(maxOccurrences) : null,
            mode,
            sourceEntryId: sourceEntryId ?? null,
            lines: lines.map(l => ({
                accountId: l.accountId,
                entryType: l.entryType,
                amount: Number(l.amount),
                description: l.description || undefined,
                segmentId: l.segmentId || undefined,
                buyerOrderId: l.buyerOrderId || undefined, // B4-0 (0-5)
                departmentId: l.departmentId || undefined, // B4-H
                projectId: l.projectId || undefined, // P1-A project
            })),
        };
        try {
            setSaving(true);
            const saved = id ? await RecurringJournalService.update(id, req) : await RecurringJournalService.create(req);
            toast.showSuccess('Recurring journal saved');
            navigate(`/accounting/recurring-journals/${saved.id}`);
        } catch (err) {
            toast.showApiError(err, 'Failed to save recurring journal');
        } finally {
            setSaving(false);
        }
    };

    const cell = (basis: number, max = 320): React.CSSProperties => ({ flex: `1 1 ${basis}px`, maxWidth: max });

    return (
        <div data-help-page="accounting.recurringForm.page" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>{id ? 'Edit recurring journal' : 'New recurring journal'}</h2>
                <Button data-help="accounting.recurringForm.back" variant="outlined" onClick={() => navigate('/accounting/recurring-journals')}>Back to list</Button>
            </div>
            {id && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Changes only affect future runs; journals already generated are not touched.
                </Alert>
            )}
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                    <TextField data-help="accounting.recurringForm.field.name" label="Name" value={name} onChange={e => setName(e.target.value)} required style={cell(240, 400)} />
                    <TextField data-help="accounting.recurringForm.field.description" label="Description (journal narration)" value={description}
                        onChange={e => setDescription(e.target.value)} style={cell(260, 520)}
                        helperText="The period is appended automatically, e.g. Office rent - Oct 2026" />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                    <TextField data-help="accounting.recurringForm.field.frequency" select label="Frequency" value={frequency}
                        onChange={e => setFrequency(e.target.value as RecurrenceFrequency)} style={cell(150, 220)}>
                        <MenuItem value="WEEKLY">Weekly</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                        <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                        <MenuItem value="YEARLY">Yearly</MenuItem>
                    </TextField>
                    <TextField data-help="accounting.recurringForm.field.interval" label="Every (interval)" type="number" value={intervalCount}
                        onChange={e => setIntervalCount(Math.max(1, Number(e.target.value) || 1))}
                        inputProps={{ min: 1, max: 52 }} style={cell(120, 160)} />
                    {frequency !== 'WEEKLY' && (
                        <TextField data-help="accounting.recurringForm.field.dayOfMonth" select label="Day of month" value={dayChoice}
                            onChange={e => setDayChoice(e.target.value)} style={cell(140, 200)}>
                            {Array.from({ length: 28 }, (_, i) => (
                                <MenuItem key={i + 1} value={String(i + 1)}>{i + 1}</MenuItem>
                            ))}
                            <MenuItem value="LAST">Last day</MenuItem>
                        </TextField>
                    )}
                    <TextField data-help="accounting.recurringForm.field.startDate" label="Start date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                        required InputLabelProps={{ shrink: true }} style={cell(160, 220)} />
                    <TextField data-help="accounting.recurringForm.field.endDate" label="End date (optional)" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} style={cell(160, 220)} />
                    <TextField data-help="accounting.recurringForm.field.maxOccurrences" label="Max occurrences (optional)" type="number" value={maxOccurrences}
                        onChange={e => setMaxOccurrences(e.target.value)} inputProps={{ min: 1 }} style={cell(160, 220)} />
                    <TextField data-help="accounting.recurringForm.field.mode" select label="When due" value={mode} onChange={e => setMode(e.target.value as RecurringMode)}
                        style={cell(200, 280)}
                        helperText={mode === 'POST'
                            ? 'Posts automatically (a draft instead if the institution requires a separate poster)'
                            : 'Creates a draft for someone to post'}>
                        <MenuItem value="DRAFT">Create a draft</MenuItem>
                        <MenuItem value="POST" disabled={!canPost}>Post automatically{!canPost ? ' (needs post permission)' : ''}</MenuItem>
                    </TextField>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    <h3>Lines</h3>
                    <Button data-help="accounting.recurringForm.addLine" variant="outlined" startIcon={<Add />}
                        onClick={() => setLines([...lines, { accountId: '', entryType: 'DEBIT', amount: 0, description: '' }])}>
                        Add line
                    </Button>
                </div>
                <ScrollableTable>
                    <table data-help-section="accounting.recurringForm.lines" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, minWidth: 980 }}>
                        <thead>
                            <tr>
                                <th data-help="accounting.recurringForm.lines.col.account" style={th}>Account</th><th data-help="accounting.recurringForm.lines.col.type" style={th}>Type</th><th data-help="accounting.recurringForm.lines.col.amount" style={th}>Amount</th>
                                <th data-help="accounting.recurringForm.lines.col.description" style={th}>Description</th><th data-help="accounting.recurringForm.lines.col.segment" style={th}>Segment</th>{orderTagging && <th data-help="accounting.recurringForm.lines.col.order" style={th}>Order</th>}{departmentOptions.length > 0 && <th data-help="accounting.recurringForm.lines.col.department" style={th}>Department</th>}{projectOptions.length > 0 && <th data-help="accounting.recurringForm.lines.col.project" style={th}>Project</th>}<th style={th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((line, i) => (
                                <tr key={i}>
                                    <td style={{ ...td, minWidth: 260 }}>
                                        <AccountAutocomplete data-help="accounting.recurringForm.line.account" data-testid={`rj-account-${i}`} accounts={leafAccounts}
                                            value={line.accountId} label="Account" placeholder="Select account"
                                            onChange={accountId => {
                                                const patch: Partial<RecurringJournalLine> = { accountId };
                                                if (isSegmentRequired(accountId, accounts) && !line.segmentId && defaultSegmentId) {
                                                    patch.segmentId = defaultSegmentId;
                                                }
                                                setLine(i, patch);
                                            }} />
                                    </td>
                                    <td style={td}>
                                        <TextField data-help="accounting.recurringForm.line.type" select size="small" value={line.entryType} fullWidth
                                            onChange={e => setLine(i, { entryType: e.target.value as 'DEBIT' | 'CREDIT' })}>
                                            <MenuItem value="DEBIT">DEBIT</MenuItem>
                                            <MenuItem value="CREDIT">CREDIT</MenuItem>
                                        </TextField>
                                    </td>
                                    <td style={td}>
                                        <TextField data-help="accounting.recurringForm.line.amount" type="number" size="small" value={line.amount} fullWidth
                                            onChange={e => setLine(i, { amount: Number(e.target.value) })}
                                            inputProps={{ min: 0, step: '0.01', 'aria-label': `Amount ${i + 1}` }} />
                                    </td>
                                    <td style={td}>
                                        <TextField data-help="accounting.recurringForm.line.description" size="small" value={line.description ?? ''} fullWidth
                                            onChange={e => setLine(i, { description: e.target.value })} />
                                    </td>
                                    <td style={td}>
                                        <SearchableSelect data-help="accounting.recurringForm.line.segment" data-testid={`rj-segment-${i}`} options={segments}
                                            value={line.segmentId ?? ''} onChange={sid => setLine(i, { segmentId: sid })}
                                            getOptionLabel={s => `${s.code} — ${s.name}`}
                                            placeholder={isSegmentRequired(line.accountId, accounts) ? 'Select segment…' : '(default)'}
                                            error={isSegmentRequired(line.accountId, accounts) && !line.segmentId} />
                                    </td>
                                    {orderTagging && (
                                        <td style={{ ...td, minWidth: 200 }}>
                                            <OrderPicker data-help="accounting.recurringForm.line.order" value={line.buyerOrderId ?? ''} label="Order"
                                                onChange={oid => setLine(i, { buyerOrderId: oid || null })} />
                                        </td>
                                    )}
                                    {departmentOptions.length > 0 && (
                                        <td style={{ ...td, minWidth: 180 }}>{/* B4-H */}
                                            <DepartmentPicker data-help="accounting.recurringForm.line.department" options={departmentOptions} value={line.departmentId ?? ''}
                                                onChange={did => setLine(i, { departmentId: did || null })} placeholder="(none)" />
                                        </td>
                                    )}
                                    {projectOptions.length > 0 && (
                                        <td style={{ ...td, minWidth: 180 }}>{/* P1-A project */}
                                            <ProjectSelect data-help="accounting.recurringForm.line.project" options={projectOptions} value={line.projectId ?? ''}
                                                currentLabel={line.projectCode ? projectTagLabel(line.projectCode, line.projectName) : null}
                                                data-testid={`rj-project-${i}`}
                                                onChange={pid => setLine(i, { projectId: pid || null })} />
                                        </td>
                                    )}
                                    <td style={td}>
                                        <IconButton data-help="accounting.recurringForm.action.deleteLine" size="small" color="error" aria-label="delete" disabled={lines.length <= 2}
                                            onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                                            <Delete />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ScrollableTable>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px 32px', marginBottom: 24 }}>
                    <div><strong>Total Debits:</strong> {formatMoney(totalDebits)}</div>
                    <div><strong>Total Credits:</strong> {formatMoney(totalCredits)}</div>
                    <div style={{ color: Math.abs(totalDebits - totalCredits) > 0.001 ? 'red' : 'green' }}>
                        <strong>Difference:</strong> {formatMoney(Math.abs(totalDebits - totalCredits))}
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button data-help="accounting.recurringForm.cancel" variant="outlined" onClick={() => navigate('/accounting/recurring-journals')}>Cancel</Button>
                    <Button data-help="accounting.recurringForm.save" type="submit" variant="contained" disabled={saving}>Save</Button>
                </div>
            </form>
        </div>
    );
};

export default RecurringJournalForm;
